<template>
  <div class="base-select-container">
    <button
      class="base-select"
      type="button"
      :class="[
        'base-select',
        {
          'base-select--error': error,
          active: multiselect ? active.length : active
        }
      ]"
      :aria-label="selectLabel"
      @click="isExpanded = !isExpanded"
    >
      <div class="base-select__text" :class="{ active: isSelected }">
        {{ selectText }}
      </div>
      <chevron-image
        class="base-select__chevron"
        alt="chevron"
        :class="{ rotate: isExpanded }"
      />
    </button>
    <div v-if="isExpanded" class="select-options">
      <div class="select-options__content">
        <template v-for="option in options">
          <base-select-nested-parent
            v-if="hasNestedOptions"
            :key="option.pk"
            :option="option"
            :active="active"
            @update-active-option="setActiveOption"
            @update-active-options="setActiveOptions"
          >
            <template v-if="option.sellers && option.sellers.length > 1">
              <base-select-option
                v-for="nestedOption in option.sellers"
                :key="nestedOption.pk"
                :option="nestedOption"
                :has-checkbox="multiselect"
                :is-active="optionIsActive(nestedOption)"
                @update-active-option="setActiveOption"
              ></base-select-option>
            </template>

            <template
              v-else-if="
                option.mostLocalSellers && option.mostLocalSellers.length > 1
              "
            >
              <base-select-option
                v-for="nestedOption in option.mostLocalSellers"
                :key="nestedOption.pk"
                :option="nestedOption"
                :has-checkbox="multiselect"
                :is-active="optionIsActive(nestedOption)"
                @update-active-option="setActiveOption"
              ></base-select-option>
            </template>
          </base-select-nested-parent>

          <base-select-option
            v-else
            :key="multiselect ? option.pk : option"
            :option="option"
            :has-checkbox="multiselect"
            :is-active="optionIsActive(option)"
            @update-active-option="setActiveOption"
          ></base-select-option>
        </template>
      </div>

      <div v-if="hasNestedOptions" class="select-options__buttons">
        <base-button
          :is-small="true"
          :is-secondary="true"
          :has-auto-width="true"
          @click.native="clearActiveOptions()"
        >
          {{ $t("shared.clear") }}
        </base-button>
        <base-button :is-small="true" @click.native="closeOptions()">
          {{ $t("shared.select") }}
        </base-button>
      </div>
    </div>
  </div>
</template>

<script>
import ChevronImage from "@/assets/images/select-chevron.svg";

import BaseSelectOption from "./BaseSelectOption";
import BaseSelectNestedParent from "./BaseSelectNestedParent";
import BaseButton from "../BaseButton/BaseButton";

export default {
  name: "BaseSelect",
  components: {
    BaseButton,
    BaseSelectNestedParent,
    BaseSelectOption,
    ChevronImage
  },
  props: {
    placeholder: {
      type: String,
      default: "Select"
    },
    options: {
      type: Array,
      default() {
        return [];
      }
    },
    multiselect: {
      type: Boolean,
      default: false
    },
    active: {
      type: [Array, String, Number, Boolean],
      default: false
    },
    hasNestedOptions: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isExpanded: false,
      activeNestedOptions: {}
    };
  },
  computed: {
    selectText() {
      return this.multiselect
        ? this.placeholder +
            ` (${
              this.active.length > 0
                ? this.active.length
                : "All countries selected"
            })`
        : this.active || this.placeholder;
    },
    selectLabel() {
      return this.isExpanded
        ? this.$t("shared.close-select-list")
        : this.$t("shared.open-select-list");
    },
    isSelected() {
      return this.multiselect ? this.active.length : this.active;
    }
  },
  methods: {
    async setActiveOptions(options) {
      let canUpdate = true;
      const optionsToPush = options.map(option => {
        const isInArray = this.active.includes(option.pk);

        if (isInArray) {
          canUpdate = false;
        }

        return option.pk;
      });

      if (!canUpdate) {
        return;
      }

      this.active.push(...optionsToPush);
      await this.$emit("update-store", this.active);
    },
    async setActiveOption(option) {
      if (this.multiselect) {
        const optionIndex = this.active.indexOf(option.pk);
        const isInArray = optionIndex === -1;

        isInArray
          ? this.active.push(option.pk)
          : this.active.splice(optionIndex, 1);

        await this.$emit("update-store", this.active);
      } else {
        this.isExpanded = false;
        await this.$emit("update-store", option);
      }
    },
    optionIsActive(option) {
      return this.multiselect
        ? this.active.includes(option.pk)
        : option === this.active;
    },
    closeOptions() {
      this.isExpanded = false;
    },
    async clearActiveOptions() {
      this.active = [];
      await this.$emit("update-store", this.active);
    }
  }
};
</script>

<style scoped lang="scss">
.base-select-container {
  position: relative;

  .base-select {
    padding: 16px 19px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border: 1px solid $color-grey-100;
    background-color: $color-white;
    width: 100%;

    &.active {
      background-color: $color-blue-100;
    }

    &__text {
      color: $color-grey-400;

      &.active {
        color: $color-black;
      }
    }

    &--error {
      background-color: $color-blue-100;
      border-color: $color-red-100;

      .base-select__text {
        color: $color-red-200;
      }
    }

    &__chevron.rotate {
      transform: rotate(180deg);
    }
  }

  .select-options {
    @include absolute(left 0 right 0);

    border: 1px solid $color-grey-100;
    border-top: transparent;
    background-color: $color-white;
    color: $color-grey-400;
    z-index: $z-index-4;

    &__content {
      overflow-y: scroll;
      max-height: 300px;
      padding: 20px 30px;
    }

    &__buttons {
      display: flex;
      border-top: 1px solid $color-grey-100;
      padding: 20px 30px;
      justify-content: flex-end;
    }

    &__option {
      width: 100%;
      display: flex;
      align-items: center;
      padding: 12px 0;
      color: $color-grey-400;

      &:hover {
        color: $color-black;
      }

      &.active {
        color: $color-black;

        .select-options__checkbox {
          position: relative;
          overflow: hidden;

          .select-options__selected {
            position: absolute;
            left: -5px;
            top: -5px;
            transform: scale(.7);
          }
        }
      }
    }

    &__checkbox {
      @include sizing(10px);

      display: inline-block;
      border: 1px solid $color-grey-100;
      margin-right: 9px;
    }
  }
}
</style>
